// Page-level error boundary. Before this existed, a single render-time crash in
// ANY page component (e.g. a null deref like `cb.capped` on /unified-projects)
// unmounted the whole React tree → blank "page doesn't work" screen, with the
// sidebar and all navigation gone. This catches such crashes, keeps the app
// chrome alive, reports to the centralized error log, and lets the user recover
// by navigating or reloading. Resets automatically on route change.
import React from "react";
import { useLocation } from "react-router-dom";
import { reportError } from "../lib/errorReporter";

class PageErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props, state) {
    // Navigating to a different route clears the error so the user isn't stuck
    // on the fallback after moving away from the broken page.
    if (props.resetKey !== state.resetKey) {
      return { hasError: false, error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, info) {
    try {
      reportError({
        message: error?.message || String(error),
        stack: error?.stack || null,
        component_stack: info?.componentStack || null,
        severity: "error",
        extra: { source: "react_error_boundary" },
      });
    } catch (_) {
      /* never throw from the boundary itself */
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center p-8 text-center">
        <div className="max-w-md space-y-3">
          <h2 className="text-lg font-semibold text-zinc-100">Something went wrong on this page</h2>
          <p className="text-sm text-zinc-400">
            This page hit an unexpected error and couldn't render. The rest of the
            app still works — try reloading, or pick another page from the menu.
            We've logged the problem automatically.
          </p>
          <div className="flex items-center justify-center gap-2 pt-1">
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-500"
            >
              Reload page
            </button>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="rounded-md border border-zinc-700 px-4 py-2 text-sm font-medium text-zinc-300 hover:bg-zinc-800"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default function PageErrorBoundary({ children }) {
  const location = useLocation();
  return <PageErrorBoundaryInner resetKey={location.pathname}>{children}</PageErrorBoundaryInner>;
}
